import { useState } from 'react'
import { Network } from 'lucide-react'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { AsyncMultiSelect } from '@/shared/ui/filters'
import { explain } from '@/shared/api/problem'
import type { SupervisorMoveResponse } from '@/shared/api/types'
import { listMobilizers } from './api'
import { ROLES, roleLabel } from './vocab'

/**
 * Changing a supervisor, deliberately.
 *
 * ## Why this is a dialog and not a drag gesture
 *
 * The move rewrites the whole subtree's `hierarchy_path` in one statement and
 * takes a lock while it does. Moving a county coordinator can shift several
 * hundred people. A drag makes that a flick of the wrist and an accident; a
 * dialog that says how many people are about to move makes it a decision.
 *
 * The count comes back in the response (`moved`), and we show it afterwards too
 * — because the number a person needs is "what did I just do", and an
 * unconfirmed action is how people stop trusting a screen.
 *
 * ## Only people who outrank them are offered
 *
 * Supervision is strictly by rank: level(supervisor) < level(subordinate). A
 * chief agent may not supervise a mobilizer even though both sit at level five,
 * because same-level supervision is exactly how a hierarchy grows the cycle the
 * path check then has to catch. PollSphere's picker promised this filter in a
 * comment and never implemented it, so peers and subordinates showed up as valid
 * choices; this one filters client-side *and* lets the server refuse, because
 * the roster's bulk import will not come through here.
 */
export function SupervisorPicker({
  mobilizerId, mobilizerName, roleCode, currentSupervisorId, onChanged, apply,
}: {
  mobilizerId: string
  mobilizerName: string
  roleCode: string
  currentSupervisorId: string | null
  onChanged: () => void
  apply: (supervisorId: string | null) => Promise<SupervisorMoveResponse>
}) {
  const [open, setOpen] = useState(false)
  const [picked, setPicked] = useState<{ value: string; label: string }[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<SupervisorMoveResponse | null>(null)

  const myLevel = ROLES[roleCode as keyof typeof ROLES]?.level ?? 99

  const run = async (supervisorId: string | null) => {
    setBusy(true); setError(null)
    try {
      const result = await apply(supervisorId)
      setDone(result)
      setPicked([])
      onChanged()
    } catch (err) { setError(explain(err)) } finally { setBusy(false) }
  }

  return (
    <>
      <button type="button" className="erp-btn" onClick={() => { setDone(null); setOpen(true) }}>
        <Network className="h-3.5 w-3.5" /> Change supervisor
      </button>

      <Modal
        open={open}
        onClose={() => { setOpen(false); setPicked([]); setError(null) }}
        title={`Who does ${mobilizerName} report to?`}
        description="Only people who outrank them are offered. A peer supervising a peer is how a reporting line grows a loop."
        footer={(
          <>
            <button type="button" className="erp-btn"
                    onClick={() => { setOpen(false); setPicked([]) }}>
              Close
            </button>
            <button
              type="button"
              className="erp-btn"
              disabled={busy || currentSupervisorId === null}
              onClick={() => void run(null)}
              title="A person with no supervisor is a root of the tree — legal, and what a national director is."
            >
              Remove supervisor
            </button>
            <button
              type="button"
              className="erp-btn-primary"
              disabled={busy || picked.length !== 1}
              onClick={() => void run(picked[0]!.value)}
            >
              {busy ? 'Moving…' : 'Move them'}
            </button>
          </>
        )}
      >
        <div className="space-y-3">
          <ErrorStrip message={error} />

          {done && (
            <Note tone="good">
              Done — {done.moved} {done.moved === 1 ? 'person' : 'people'} moved, including
              everyone reporting to {mobilizerName}.
            </Note>
          )}

          <AsyncMultiSelect
            label="Supervisor"
            selected={picked}
            onChange={(next) => setPicked(next.slice(-1))}
            placeholder="Search by name"
            search={async (term) => {
              const page = await listMobilizers({ q: term, status: 'ACTIVE' }, undefined, 25)
              return page.content
                .filter((m) => m.id !== mobilizerId)
                .filter((m) => (ROLES[m.roleCode]?.level ?? 99) < myLevel)
                .map((m) => ({
                  value: m.id,
                  label: m.fullName,
                  sub: `${roleLabel(m.roleCode)} · ${m.primaryUnitName ?? 'no unit'}`,
                }))
            }}
          />

          {busy && <LoadingBlock label="Rewriting the reporting line…" />}

          <p className="text-erp-xs text-muted-foreground">
            This moves {mobilizerName} <em>and everyone under them</em>. The response says
            exactly how many people that was; if it is more than you expected, that is the
            number to check before doing anything else.
          </p>
        </div>
      </Modal>
    </>
  )
}
